import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Calendar, ArrowRight, Loader2, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import Header from './Header';
import Footer from './Footer';
import { articulosBlog as mockArticulos } from '../data/mock';

const BlogPage = () => {
  const navigate = useNavigate();
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [categoriaActiva, setCategoriaActiva] = useState('Todas');
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    cargarArticulos();
    cargarCategorias();
  }, []);

  const cargarArticulos = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/blog?publicado=true`);
      const data = await response.json();

      if (data.success && data.articulos.length > 0) {
        setArticulos(data.articulos);
      } else {
        setArticulos(mockArticulos);
      }
    } catch (err) {
      console.error('Error cargando artículos:', err);
      setArticulos(mockArticulos);
    } finally {
      setLoading(false);
    }
  };

  const cargarCategorias = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/blog-categorias`);
      const data = await response.json();

      if (data.success && data.categorias.length > 0) {
        setCategorias(['Todas', ...data.categorias]);
      } else {
        const categoriasUnicas = [...new Set(mockArticulos.map(a => a.categoria))];
        setCategorias(['Todas', ...categoriasUnicas]);
      }
    } catch (err) {
      const categoriasUnicas = [...new Set(mockArticulos.map(a => a.categoria))];
      setCategorias(['Todas', ...categoriasUnicas]);
    }
  };

  const irAlArticulo = (slug) => {
    navigate(`/blog/${slug}`);
  };

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Filtrar artículos por categoría y búsqueda
  const articulosFiltrados = articulos.filter(articulo => {
    const coincideCategoria = categoriaActiva === 'Todas' || articulo.categoria === categoriaActiva;
    const coincideBusqueda = busqueda === '' || 
      articulo.titulo.toLowerCase().includes(busqueda.toLowerCase()) ||
      articulo.extracto.toLowerCase().includes(busqueda.toLowerCase());
    return coincideCategoria && coincideBusqueda;
  });

  return (
    <div className='min-h-screen bg-[var(--bg-page)]'>
      <Header />
      
      <div className='container mx-auto px-4 py-24 md:py-32'>
        <div className='max-w-6xl mx-auto'>
          <div className='text-center mb-12'>
            <div className='flex items-center justify-center gap-2 mb-4'>
              <BookOpen size={48} className='text-[var(--brand-dark)]' />
              <h1 className='heading-1'>Blog de Osteopatía</h1>
            </div>
            <p className='body-large max-w-2xl mx-auto'>
              Guías completas sobre tratamientos osteopáticos para diferentes lesiones y dolencias musculoesqueléticas
            </p>
          </div>

          {/* Búsqueda y Filtros */}
          <div className='mb-8 space-y-4'>
            <div className='relative max-w-md mx-auto'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-light)]' size={20} />
              <Input
                type='text'
                placeholder='Buscar artículos...'
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className='pl-10 rounded-full border-[var(--border-light)]'
                data-testid="buscar-articulos"
              />
            </div>
            
            <div className='flex flex-wrap justify-center gap-2'>
              {categorias.map((categoria) => (
                <Button
                  key={categoria}
                  variant={categoriaActiva === categoria ? 'default' : 'outline'}
                  className={`rounded-full ${categoriaActiva === categoria ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setCategoriaActiva(categoria)}
                  data-testid={`filtro-${categoria.toLowerCase().replace(/ /g, '-')}`}
                >
                  {categoria}
                </Button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="animate-spin text-[var(--brand-dark)]" size={48} />
              <span className="ml-4 body-large">Cargando artículos...</span>
            </div>
          ) : articulosFiltrados.length === 0 ? (
            <div className='text-center py-12'>
              <p className='body-large text-[var(--text-light)]'>
                No se encontraron artículos que coincidan con tu búsqueda.
              </p>
              <Button 
                className='btn-secondary mt-4' 
                onClick={() => { setBusqueda(''); setCategoriaActiva('Todas'); }}
              >
                Limpiar filtros
              </Button>
            </div>
          ) : (
            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {articulosFiltrados.map((articulo) => (
                <Card 
                  key={articulo.id || articulo.slug} 
                  className='network-card cursor-pointer'
                  onClick={() => irAlArticulo(articulo.slug)}
                  data-testid={`blog-card-${articulo.slug}`}
                >
                  <CardHeader className='p-0'>
                    <div className='relative aspect-video rounded-t-[32px] overflow-hidden bg-[var(--bg-section)]'>
                      <img 
                        src={articulo.imagen} 
                        alt={articulo.titulo}
                        className='w-full h-full object-cover hover:scale-105 transition-transform duration-300'
                      />
                      <div className='absolute top-3 left-3 bg-[var(--brand-primary)] text-[var(--brand-dark)] px-3 py-1 rounded-full text-sm font-semibold'>
                        {articulo.categoria}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className='pt-4'>
                    <div className='flex items-center gap-2 text-[var(--text-light)] mb-3'>
                      <Calendar size={16} />
                      <span className='body-small'>{formatearFecha(articulo.fecha || articulo.created_at)}</span>
                    </div>
                    <CardTitle className='heading-3 mb-3 line-clamp-2'>{articulo.titulo}</CardTitle>
                    <CardDescription className='body-medium line-clamp-3'>
                      {articulo.extracto}
                    </CardDescription>
                    <div className='mt-4'>
                      <Button variant='link' className='p-0 h-auto text-[var(--brand-dark)] font-semibold'>
                        Leer más <ArrowRight className='ml-1 h-4 w-4' />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default BlogPage;
